<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { SquareUser, LogOut, ChevronRight, EarthLock, Eraser, ReceiptText, Handshake } from 'lucide-vue-next';
import { formatPhoneNumber, logout } from '@/lib/utils';
import {
    Drawer,
    DrawerTrigger,
    DrawerContent,
    DrawerHeader,
    DrawerDescription,
    DrawerClose,
    DrawerFooter,
} from '@/components/ui/drawer';
import { ScrollArea } from '@/components/ui/scroll-area';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

import { useRoute } from 'vue-router';
import { useDrawerScrollLock } from '@/composables/useDrawerScrollLock';

const route = useRoute();

type ToSResponse = {
    data: {
        content: string;
    }
} | {
    message: string;
};

const termsOfService = ref<string | null>(null);

// use composable for drawer scroll lock handling
const { isOpen: drawerOpen } = useDrawerScrollLock();

onMounted(async () => {
    const result = await axios.get('/getToS') as ToSResponse;
    if ('data' in result) {
        const content = result.data.content;
        const emptyParagraphRegex = /<p\s?(dir="ltr")?>\s*<\/p>/ig;
        termsOfService.value = content.replace(emptyParagraphRegex, '').replace(emptyParagraphRegex, '');
    }
});

const phone = localStorage.getItem('phone') || '';
</script>

<template>
    <div class="flex flex-col gap-10">
        <div>
            <div class="text-gray-900 text-xl mb-2.5">Account</div>
            <div class="bg-white rounded-[20px] shadow overflow-hidden">
                <div class="flex items-center gap-3 py-4 px-5">
                    <SquareUser class="size-5 text-gray-700" />
                    <div class="flex flex-col gap-1">
                        <div class="text-gray-900 text-md font-semibold">Your phone number</div>
                        <div class="text-gray-400 font-medium">{{ formatPhoneNumber(phone) }}</div>
                    </div>
                </div>
                <div class="border-t border-gray-200"></div>
                <div class="flex items-center gap-3 py-4 px-5 cursor-pointer" @click="logout(route)">
                    <LogOut class="size-5 text-gray-700"/>
                    <div class="text-gray-900 text-md font-semibold">Sign out</div>
                </div>
            </div>
        </div>
        <div>
            <div class="text-gray-900 text-xl mb-2.5">Documents</div>
            <div class="bg-white rounded-[20px] shadow overflow-hidden">
                <a href="https://www.avee.health/terms-and-conditions" target="_blank" class="flex items-center gap-3 py-4 px-5">
                    <Handshake class="size-5 text-gray-700"/>
                    <div class="text-gray-900 text-md font-semibold">Terms & Conditions</div>
                    <ChevronRight class="ml-auto text-gray-700" />
                </a>
                <div class="border-t border-gray-200"></div>
                <a href="https://www.avee.health/privacy-policy" target="_blank" class="flex items-center gap-3 py-4 px-5">
                    <EarthLock class="size-5 text-gray-700"/>
                    <div class="text-gray-900 text-md font-semibold">Privacy Policy</div>
                    <ChevronRight class="ml-auto text-gray-700" />
                </a>
                <div class="border-t border-gray-200"></div>
                <Drawer v-model:open="drawerOpen">
                    <DrawerTrigger class="w-full">
                        <div class="flex items-center gap-3 py-4 px-5">
                            <ReceiptText class="size-5 text-gray-700"/>
                            <div class="text-gray-900 text-md font-semibold text-balance text-left">Patient Acknowledgment and Agreement</div>
                            <ChevronRight class="ml-auto text-gray-700 shrink-0" />
                        </div>
                    </DrawerTrigger>
                    <DrawerContent>
                        <ScrollArea class="h-screen">
                            <DrawerHeader>
                                <DrawerDescription>
                                    <div v-html="termsOfService" class="max-w-screen-md mx-auto p-2 pb-20 md:py-20 prose prose-hr:my-6 text-gray-800 prose-p:my-2 prose-li:list-disc" />
                                </DrawerDescription>
                            </DrawerHeader>
                            <DrawerFooter class="fixed bottom-0 w-full backdrop-blur-sm bg-white/50">
                                <DrawerClose>
                                    <Button variant="outline">Close</Button>
                                </DrawerClose>
                            </DrawerFooter>
                        </ScrollArea>
                    </DrawerContent>
                </Drawer>
            </div>
        </div>
        <div>
            <div class="bg-background rounded-[20px] shadow overflow-hidden">
                <AlertDialog>
                    <AlertDialogTrigger as-child>
                        <div class="flex items-center gap-3 py-4 px-5 cursor-pointer bg-gray-100 border-dashed border rounded-[20px] !border-gray-300">
                            <Eraser class="size-5 text-gray-700"/>
                            <div class="text-gray-900 text-md font-semibold">Delete Account</div>
                        </div>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Are you sure you want to delete your account?</AlertDialogTitle>
                        <AlertDialogDescription>Your account will be deleted after 14 days. You can cancel anytime before then.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction @click="logout(route)">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        </div>
    </div>
</template>
